import "@tento-ui/components/video/hls-player";
import "@tento-ui/components/video/youtube-playlist";
import "@tento-ui/components/video/video-edit-modal";
import "@tento-ui/components/transition";

import { AsyncStateManager } from "@tento-core/async-state-manager";
import { Tracing } from "@tento-core/tracing";
import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { Style, css } from "@tento-ui/component-styles";
import { Button } from "@tento-ui/components/button/button";
import { SheetLegend } from "@tento-lona/sheets-ui/sheet/sheet-legend.ts";
import {
  type BindCellContext,
  type RenderPlugin,
  RowOptionsElement,
  type SheetLegendClass,
} from "@tento-lona/sheets-ui/sheet/render-plugin.ts";
import { CellTarget, type SheetRow } from "@tento-lona";
import { Actor, Json, type Option } from "@tento-lona/sheets";
import { Transition } from "@tento-ui/components/transition";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { LonaIcons, PlayIcon } from "@tento-ui/ui/icons";
import { NaiveDate } from "@tento-chrono";

import { YoutubePlaylist } from "@tento-ui/components/video/youtube-playlist";
import { YoutubeUrl } from "@tento-ui/components/video/youtube-url";
import { VideoEditModal } from "@tento-ui/components/video/video-edit-modal";
import type { SheetDelegate } from "@tento-lona/sheets-ui/sheet/sheet-delegate.ts";
import type { Size } from "@tento-ui/layout-utils";
import { COLUMN_VIDEO_SPEC } from "@tento-lona/sheets/platform";
import type { PerTypeSpec } from "@tento-lona/sheets/platform";
import { storageCellDateForColumn } from "./cell-date.ts";

/** The renderer plugin for a video row: a playlist per column. */
export class ColumnVideoRendererPlugin implements RenderPlugin.Renderer<
  VideoCell,
  SheetLegend.DefaultRow
> {
  static readonly spec: PerTypeSpec = COLUMN_VIDEO_SPEC;

  readonly role = "renderer";
  readonly id = "column.video";
  readonly icon: HTMLTemplateElement = LonaIcons.$template(PlayIcon);

  static readonly #MAX_URL_LENGTH = 2048;
  public static readonly MAX_VIDEO_URLS = 10;

  #stateManager = new AsyncStateManager();
  constructor(
    readonly makeRowOptions: RowOptionsElement.Factory,
  ) {}

  static #sanitizeUrl(url: string): Option<string> {
    const trimmed = url.trim();
    if (trimmed.length === 0) return null;
    if (trimmed.length > ColumnVideoRendererPlugin.#MAX_URL_LENGTH) return null;

    const hashIndex = trimmed.indexOf("#");
    if (hashIndex !== -1) return trimmed.substring(0, hashIndex);
    return trimmed;
  }

  optionsInfo(_row: SheetRow.Joined.Handle): Option<string> {
    return "Store urls of videos for a given date column. Use this to save daily vlogs or class lectures.";
  }

  makeCell(): VideoCell {
    return VideoCell.make();
  }

  bindCell(
    $cell: VideoCell,
    {
      sheet: rowList,
      row,
      sheetRow,
      column,
      widthPx,
    }: BindCellContext,
  ): void {
    const time = column.discrete();
    const heightPx = row.layoutSpec(rowList.timescale).cellHeight;
    // Reads use the visual column's range, which holds the one canonical
    // storage period whose start falls in it. A same-granularity write must
    // key THAT period (`storageCellDateForColumn`, as column.text/number/
    // checkmark do), never the visual partial: a Sunday-start week column's
    // `yw-…` names the ISO week ending on its Sunday, which the wire re-parses
    // Monday-anchored, so the playlist came back one column left of where it
    // was saved. A spanned or custom row keeps the visual key it reads.
    const persistDate = sheetRow.timescaleSetting === time[0].type
      ? storageCellDateForColumn(sheetRow.timescaleSetting, column)
      : time[0];

    // A video row has one cell per column: its single-cardinality facade
    // writes it; a refusal is reported, never left floating.
    const cells = row.writableSingle();
    const report = (err: unknown) =>
      Tracing.warn("net")("source-video: write failed", { rowId: row.id.toString(), error: String(err) });
    const bindWithData = (data: Option<string>) => {
      void $cell.bind(time[0], data, widthPx, heightPx, (urls: string[]) => {
        if (cells == null) return;
        if (urls.length === 0) {
          cells.clearDurable(CellTarget.date(persistDate), Actor.EXTERNAL).settled.catch(report);
          return;
        }

        const sanitizedUrls: string[] = [];
        for (const url of urls) {
          const sanitized = ColumnVideoRendererPlugin.#sanitizeUrl(url);
          if (sanitized) sanitizedUrls.push(sanitized);
        }

        if (sanitizedUrls.length === 0) {
          cells.clearDurable(CellTarget.date(persistDate), Actor.EXTERNAL).settled.catch(report);
          return;
        }

        cells.writeDurable(CellTarget.date(persistDate), JSON.stringify(sanitizedUrls), Actor.EXTERNAL).settled.catch(report);
      });
    };

    // Same-type cells are recycled in place across columns without
    // `onCellRecycled` firing, and `onDataFull` below is deferred — so
    // until it fires, a recycled cell's edit modal (and its onSave
    // persist target) still points at the PREVIOUS column's week. Bind
    // synchronously from the cached value so the modal opens on, and
    // persists to, this column's week (same hazard `983f2c5` fixed for
    // column.text; `stillValid` only blocks stale fires, it does not
    // install the new week's callback early).
    const single = row.single();
    if (single == null) return;
    bindWithData((single.peek(time[0])?.value as string) ?? null);

    const cellHandle = single.cell(time[0]);
    const stillValid = this.#stateManager.bind($cell, cellHandle);
    cellHandle.onDataFull((cell) => {
      if (!stillValid(cellHandle)) return;
      bindWithData((cell?.value as string) ?? null);
    });
  }

  onResize(_$cell: VideoCell, _context: BindCellContext): void {}

  loadData = null;

  legendFactory: SheetLegendClass<SheetLegend.DefaultRow> = SheetLegend.DefaultFactory;

  onBindRow(_sheet: SheetDelegate, _row: SheetRow.Joined.Handle): void {}
  onViewportColumnsChanged(): void {}
  onRenderedColumnsWillChange(): void {}
}

/** Types that go with `ColumnVideoRendererPlugin`: `Attributes`. */
export namespace ColumnVideoRendererPlugin {
  export type Attributes = {};
}

/** The `lona-video-cell` element: one day's cell of a video row, playing that day's links as a YouTube playlist. */
@component({
  name: "lona-video-cell",
})
export class VideoCell extends LonaWebComponent {
  $youtubePlaylist: YoutubePlaylist = this.hydrate("youtube-playlist");
  $transition: Transition = this.hydrate("transition");

  props: Option<{
    onUpdateLinks: (links: string[]) => void;
    date: NaiveDate.Partial;
    urls: YoutubeUrl[];
  }> = null;

  onSizeChange: Option<(s: Size) => void> = (size: Size) => {
    if (size.height > 0) {
      this.$youtubePlaylist.style.height = `${size.height - 2}px`;
    }
  };

  #showEditLinkFlow() {
    VideoEditModal.makeWith({
      date: this.props?.date.toStringReadable() ?? "",
      urls: this.props?.urls.map((u) => u.url.toString()) ?? [],
      maxUrls: ColumnVideoRendererPlugin.MAX_VIDEO_URLS,
      onSave: (urls) => {
        this.props?.onUpdateLinks?.(urls);
      },
    }).reveal();
  }

  async bind(
    date: NaiveDate.Partial,
    urlsData: Option<string>,
    _widthPx: number,
    heightPx: number,
    onUpdateLinks: (links: string[]) => void,
  ) {
    const urls: YoutubeUrl[] = [];

    if (urlsData) {
      let urlStrings: string[] = [];

      if (urlsData.trim().startsWith("[")) {
        try {
          const parsed = Json.wire(JSON.parse(urlsData));
          urlStrings = Array.isArray(parsed)
            ? parsed.filter((url: Json): url is string => typeof url === "string")
            : [urlsData];
        } catch {
          urlStrings = [urlsData];
        }
      } else {
        urlStrings = [urlsData];
      }

      for (const urlStr of urlStrings) {
        const parsed = YoutubeUrl.parse(urlStr);
        if (parsed) urls.push(parsed);
      }
    }

    this.props = {
      date,
      onUpdateLinks,
      urls,
    };

    const hasVideos = urls.length > 0;
    this.$("root").assignStyles({
      background: hasVideos ? "black" : "transparent",
    });
    GESTURE_MANAGER.addPointerEvent(this.$("add-video-btn"), {
      onClick: () => this.#showEditLinkFlow(),
    });

    this.$transition.bindState(hasVideos ? "after" : "before");

    this.$youtubePlaylist.style.height = `${heightPx - 2}px`;
    this.$youtubePlaylist.bind({
      urls: urls,
      selectedIndex: 0,
      showIndicator: true,
      showNavBar: false,
      showAddButton: true,
      onAdd: () => this.#showEditLinkFlow(),
    });
  }

  static $styles: Style.Compat[] = [
    Button.$style,
    css`
      :host {
        height: 100%;
      }

      #root {
        position: relative;
        border-radius: 8px;
        height: calc(100% - 2px);
        overflow: hidden;
        margin: 1px;
      }

      #transition {
        height: 100%;
      }

      #add-link {
        padding: 12px;
        align-items: center;
        height: 100%;
      }

      #after-container {
        height: 100%;
      }

      #youtube-playlist {
        --add-button-bg: var(--white12);
        --add-button-color: var(--white50);
        --carousel-indicator-bg: var(--white12);
        --carousel-indicator-bg-hover: var(--white20);
        --carousel-indicator-color: var(--white50);
        --scrollbar-bg: var(--white12);
        --scrollbar-thumb-bg: var(--white50);
        --scrollbar-thumb-bg-hover: var(--white80);
        padding: 2px;
      }

      #add-video-btn {
        color: var(--text-ter);
      }
    `,
  ];
  static $html: Option<HTMLTemplateElement> = html`
    <std-col id="root">
      <std-transition id="transition">
        <std-center slot="before" id="add-link">
          <button id="add-video-btn" secondary>
            <h1 class="p">+ Add a video</h1>
          </button>
        </std-center>
        <std-col slot="after" id="after-container">
          <std-youtube-playlist id="youtube-playlist"></std-youtube-playlist>
        </std-col>
      </std-transition>
    </std-col>
  `;
}
